import { Badge, Group, Text, Tooltip } from '@mantine/core';

import type { CandidateResult } from '../hooks/useModelArena';
import { modelLabel } from '../runLabels';

const servedLabel = (result: CandidateResult) => `${result.callsServed}/${result.calls}`;

import { type Column, DataTable, type Row } from './DataTable';
import { orderMetricNames, readable } from './metricFormat';

/**
 * What each candidate scored, side by side. Every column sorts and the categorical ones filter,
 * because that behaviour lives in DataTable rather than being written again here.
 */
export function Leaderboard({
  results,
  rankMetric,
}: {
  results: CandidateResult[];
  rankMetric: string | null;
}) {
  const metricNames = orderMetricNames(results, rankMetric);
  const byModel = Object.fromEntries(results.map(result => [modelLabel(result), result]));

  const columns: Column[] = [
    { key: 'model', label: 'Model' },
    ...metricNames.map(name => ({ key: name, label: readable(name), numeric: true })),
    {
      key: 'served',
      label: 'Calls served',
      numeric: true,
      render: row => <ServedCell result={byModel[String(row.model)]} />,
      text: row => servedLabel(byModel[String(row.model)]),
    },
    { key: 'failures', label: 'Failures', numeric: true },
    {
      key: 'seconds',
      label: 'Time',
      numeric: true,
      render: row => (
        <Text size="xs" c="dimmed">
          {row.seconds === null ? '—' : `${row.seconds}s`}
        </Text>
      ),
      text: row => (row.seconds === null ? '—' : `${row.seconds}s`),
    },
  ];

  const rows: Row[] = results.map(result => ({
    model: modelLabel(result),
    ...Object.fromEntries(metricNames.map(name => [name, result.metrics[name] ?? null])),
    // Sorted by how much of the work the candidate answered itself, not by the string shown.
    served: result.calls === 0 ? 0 : result.callsServed / result.calls,
    failures: result.failures,
    seconds: result.wallClockSeconds,
  }));

  return <DataTable columns={columns} rows={rows} minWidth={640} emphasiseFirstColumn />;
}

/** Anything short of every call means another model served part of the work, so the score is not
 * purely this model's. */
function ServedCell({ result }: { result: CandidateResult }) {
  const clean = result.fallbackRate === 0;
  const others = Object.entries(result.servedByOthers);

  const cell = (
    <Text size="xs" c={clean ? 'dimmed' : 'orange'} ff="monospace">
      {servedLabel(result)}
    </Text>
  );

  if (clean) return cell;

  return (
    <Tooltip
      multiline
      w={260}
      label={
        others.length
          ? `Fell back to ${others.map(([model, count]) => `${model} (${count})`).join(', ')}`
          : 'Some calls were not served by this model'
      }
    >
      <Group justify="flex-end" gap={4} wrap="nowrap">
        {cell}
        <Badge size="xs" color="orange" variant="light">
          fallback
        </Badge>
      </Group>
    </Tooltip>
  );
}
